import { useEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { V4 } from '../tokens';
import { ROLE_CTA } from '@utils/featureRollout/strings';
import type { FeatureVariant } from '@utils/featureRollout/types';

// Card reutilizable del framework de rollout (generaliza el molde VersionPickerCard).
// Dos modos según la variante activa:
//   - control   → invitación "Prueba la nueva versión" (acción: probar treatment).
//   - treatment → "Regresar a la versión anterior" (acción: revert a control), si can_revert.
//
// CTA POR ROL (Item E#1, decisión EN RENDER con isRoot resuelto):
//   - root    → "Dejar activa la versión nueva" → switch DURABLE per-comercio (onTry).
//   - no-root → "Echa un vistazo" → vista previa PERSONAL per-usuario (onPreview), sin tocar el
//               comercio. Ya NO se bloquea al no-root con un aviso tras el click: el operador
//               tiene una acción real (mirar la nueva) en lugar de un CTA muerto.
//
// V4: zero Tailwind, 100% inline styles con tokens.

export interface FeatureVersionCardProps {
  variant: FeatureVariant;
  canRevert: boolean;
  gaForced: boolean;
  isRoot: boolean;
  // ROOT: dispara el switch DURABLE (fire-and-forget). El consumidor llama switchTo del hook.
  onTry: () => void;
  // NO-ROOT: enciende la vista previa per-usuario (no toca el comercio). El consumidor llama
  // startPreview. Si no se provee, el no-root cae a onTry (compatibilidad).
  onPreview?: () => void;
  onRevert: () => void;
  // Se llama una vez cuando la invitación es visible (telemetría invitation_shown).
  onShown?: () => void;
  newLabel?: string;
  newDescription?: string;
}

const SparkIcon: ReactNode = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
  </svg>
);

const BackIcon: ReactNode = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M11 5l-7 7 7 7M4 12h16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon: ReactNode = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const cardStyle: CSSProperties = {
  background: V4.white,
  borderRadius: 10,
  border: `1px solid ${V4.grayLight}`,
  overflow: 'hidden',
  fontFamily: 'inherit',
};

const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  background: V4.secondary,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '9px 14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
  // No encoger el botón ni partir su label: cuando no cabe en la fila, el contenedor
  // (flexWrap) lo baja a su propia línea en vez de exprimir la columna de texto a ~0.
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

// Treatment: pill discreto para "volver" (no un botón de banner). Slim, redondeado.
const revertPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.white,
  color: V4.secondaryDark,
  border: `1px solid ${V4.secondaryLight2}`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 700,
};

// Etiqueta compacta de estado "estás en la versión nueva": acento success para que se
// note (no pasa desapercibida) sin ocupar un banner que rompa el layout de V2.
const newTag: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  background: V4.successFaded,
  color: V4.successDark,
  borderRadius: 999,
  padding: '5px 10px',
  fontFamily: 'inherit',
  fontSize: 11.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

export const FeatureVersionCard: FC<FeatureVersionCardProps> = ({
  variant,
  canRevert,
  gaForced,
  isRoot,
  onTry,
  onPreview,
  onRevert,
  onShown,
  newLabel = 'Prueba la nueva versión',
  newDescription = 'Rediseñamos esta sección. Pruébala cuando quieras y regresa a la anterior si lo prefieres.',
}) => {
  // softNotice solo aplica al REVERT durable (treatment→control), que sigue siendo root-only.
  // El CTA de control ya NO bloquea al no-root: le ofrece la vista previa personal.
  const [softNotice, setSoftNotice] = useState<string | null>(null);
  const shownRef = useRef(false);

  // Telemetría invitation_shown: una vez al montar en estado invitación/regreso.
  // Usamos un ref como guard para que el efecto pueda depender de onShown sin re-disparar.
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    onShown?.();
  }, [onShown]);

  // Si la feature graduó a GA (ga_forced) ya no se ofrece regresar: no renderizamos card.
  if (gaForced) return null;

  // Guard del REVERT durable (root-only): revertir el switch del comercio es per-comercio.
  const guardRoot = (action: () => void) => {
    if (!isRoot) {
      setSoftNotice('Solo el administrador principal (root) puede cambiar la versión de este comercio.');
      return;
    }
    setSoftNotice(null);
    action();
  };

  const isTreatment = variant === 'treatment';

  // En treatment solo mostramos "regresar" si el backend lo permite (can_revert).
  if (isTreatment && !canRevert) return null;

  // Treatment: barra DISCRETA y descubrible, alineada a la derecha. No es un banner que
  // empuje/rompa el layout de V2; es solo el acceso a "volver" + una etiqueta de estado
  // con acento success para que se note. El anuncio grande se reserva para la invitación.
  if (isTreatment) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <span style={newTag}>
            {CheckIcon}
            Estás en la versión nueva
          </span>
          <button type="button" style={revertPill} onClick={() => guardRoot(onRevert)}>
            {BackIcon}
            Volver a la anterior
          </button>
        </div>
        {softNotice && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              borderRadius: 8,
              background: V4.warningFaded,
              color: V4.warningDark,
              fontSize: 12,
              fontWeight: 600,
              textAlign: 'right',
            }}
          >
            {softNotice}
          </div>
        )}
      </div>
    );
  }

  // Control: invitación a probar la nueva versión. Aquí SÍ es una card con presencia
  // (es una oferta y debe destacar para que el comercio la descubra y acepte).
  return (
    <div style={cardStyle}>
      {/* flexWrap: en viewports angostos el botón (flexShrink:0, label sin partir) baja a su
          propia línea en vez de exprimir la columna de texto a ~0 (que la partía palabra por
          palabra y descuadraba la card empujando el contenido). */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '12px 14px' }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: V4.grayBlueLight,
            color: V4.secondary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {SparkIcon}
        </span>
        {/* minWidth da un piso a la columna de texto: nunca colapsa a una palabra por renglón;
            si no cabe junto al botón, el botón es el que envuelve a la línea siguiente. */}
        <span style={{ flex: 1, minWidth: 160 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: V4.primary }}>{newLabel}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: V4.grayUltraDark, marginTop: 2, lineHeight: 1.4 }}>
            {newDescription}
          </span>
        </span>
        {/* CTA POR ROL (decisión EN RENDER con isRoot ya resuelto): root deja activa la versión
            nueva (durable); no-root echa un vistazo (vista previa personal, sin tocar el comercio).
            Si no hay onPreview, el no-root cae a onTry (compatibilidad). */}
        <button
          type="button"
          style={primaryButton}
          onClick={() => (isRoot ? onTry() : onPreview ? onPreview() : onTry())}
        >
          {SparkIcon}
          {isRoot ? ROLE_CTA.rootTry : ROLE_CTA.operatorPreview}
        </button>
      </div>
    </div>
  );
};

export default FeatureVersionCard;
